import React, { useState, useEffect } from 'react';
import { useTheme, THEMES } from '../../contexts/ThemeContext';

/**
 * ThemeSelectorPanel - Premium Theme Selection Experience
 */
const ThemeSelectorPanel = ({ isOpen, onClose }) => {
  const {
    themeId,
    setTheme,
    resetTheme
  } = useTheme();

  const [selectedTheme, setSelectedTheme] = useState(themeId);

  // Sync selected theme with current theme
  useEffect(() => {
    setSelectedTheme(themeId);
  }, [themeId]);

  // Handle theme selection - only on click
  const handleThemeSelect = (id) => {
    setSelectedTheme(id);
    setTheme(id);
  };

  // Handle reset to default
  const handleReset = () => {
    resetTheme();
    setSelectedTheme('classic');
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const themes = Object.values(THEMES);

  return (
    <>
      {/* Backdrop */}
      <div
        className="theme-panel-backdrop"
        onClick={onClose}
      />

      {/* Panel */}
      <div className={`theme-panel ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="theme-panel-header">
          <div className="theme-panel-title-section">
            <span className="theme-panel-icon">🎨</span>
            <div>
              <h2 className="theme-panel-title">בחירת עיצוב</h2>
              <p className="theme-panel-subtitle">כל עיצוב משנה את המראה והתחושה של כל המערכת</p>
            </div>
          </div>
          <button
            className="theme-panel-close"
            onClick={onClose}
            aria-label="סגור"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="theme-panel-content">
          {/* Theme list */}
          <div className="theme-list">
            {themes.map((theme) => (
              <ThemeCard
                key={theme.id}
                theme={theme}
                isSelected={selectedTheme === theme.id}
                isCurrent={themeId === theme.id}
                onSelect={() => handleThemeSelect(theme.id)}
              />
            ))}
          </div>

          {/* Reset button */}
          {themeId !== 'classic' && (
            <button
              className="theme-reset-btn"
              onClick={handleReset}
            >
              <span>↺</span>
              חזרה לעיצוב ברירת מחדל
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="theme-panel-footer">
          <p className="theme-footer-note">
            העיצוב נשמר אוטומטית והוא יוחל מיידית על כל הדפים
          </p>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .theme-panel-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9998;
          animation: fadeIn 0.2s ease;
          backdrop-filter: blur(2px);
        }

        .theme-panel {
          position: fixed;
          top: 0;
          left: 0;
          width: 440px;
          max-width: 90vw;
          height: 100vh;
          background: var(--color-bg-secondary, #fff);
          box-shadow: 8px 0 32px rgba(0, 0, 0, 0.2);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          transform: translateX(-100%);
          animation: slideIn 0.3s ease forwards;
        }

        .theme-panel.open {
          transform: translateX(0);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        /* Header */
        .theme-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px;
          border-bottom: 1px solid var(--color-border-primary, #e5e7eb);
          background: var(--color-bg-tertiary, #f9fafb);
        }

        .theme-panel-title-section {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .theme-panel-icon {
          font-size: 32px;
        }

        .theme-panel-title {
          margin: 0;
          font-size: 22px;
          font-weight: 700;
          color: var(--color-text-primary, #111);
        }

        .theme-panel-subtitle {
          margin: 4px 0 0;
          font-size: 13px;
          color: var(--color-text-tertiary, #6b7280);
        }

        .theme-panel-close {
          width: 36px;
          height: 36px;
          border: none;
          background: transparent;
          font-size: 20px;
          color: var(--color-text-tertiary, #6b7280);
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .theme-panel-close:hover {
          background: var(--color-bg-hover, #f3f4f6);
          color: var(--color-text-primary, #111);
        }

        /* Content */
        .theme-panel-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        /* Theme list */
        .theme-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* Reset button */
        .theme-reset-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 14px;
          margin-top: 24px;
          background: transparent;
          border: 2px dashed var(--color-border-primary, #d1d5db);
          border-radius: 10px;
          color: var(--color-text-tertiary, #6b7280);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .theme-reset-btn:hover {
          border-color: var(--color-primary, #3b82f6);
          color: var(--color-primary, #3b82f6);
          background: var(--color-primary-light, #eff6ff);
        }

        .theme-reset-btn span {
          font-size: 18px;
        }

        /* Footer */
        .theme-panel-footer {
          padding: 18px 24px;
          border-top: 1px solid var(--color-border-primary, #e5e7eb);
          background: var(--color-bg-tertiary, #f9fafb);
        }

        .theme-footer-note {
          margin: 0;
          font-size: 13px;
          color: var(--color-text-tertiary, #6b7280);
          text-align: center;
        }
      `}</style>
    </>
  );
};

/**
 * ThemeCard - Premium Theme Selection Card with Full Preview
 */
const ThemeCard = ({ theme, isSelected, isCurrent, onSelect }) => {
  return (
    <button
      className={`theme-card ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
      onClick={onSelect}
    >
      {/* Full Preview Mockup */}
      <div
        className="theme-card-preview"
        style={{
          background: theme.colors.bgPrimary,
        }}
      >
        {/* Full Page Mockup */}
        <div className="preview-page">
          {/* Header */}
          <div
            className="mockup-header"
            style={{
              background: theme.colors.bgHeader,
              height: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 6px',
              gap: '4px'
            }}
          >
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: theme.colors.primary, opacity: 0.8 }} />
            <div style={{ flex: 1 }} />
            <div style={{ width: '16px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.3)' }} />
          </div>

          {/* Body with sidebar */}
          <div className="mockup-body" style={{ display: 'flex', flex: 1 }}>
            {/* Sidebar */}
            <div
              className="mockup-sidebar"
              style={{
                width: '32px',
                background: theme.colors.bgSidebar,
                borderRight: `1px solid ${theme.colors.borderPrimary}`,
                padding: '6px 4px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}
            >
              <div style={{ height: '6px', borderRadius: theme.shape?.radiusSm || '2px', background: theme.colors.primary }} />
              <div style={{ height: '6px', borderRadius: theme.shape?.radiusSm || '2px', background: theme.colors.textTertiary, opacity: 0.3 }} />
              <div style={{ height: '6px', borderRadius: theme.shape?.radiusSm || '2px', background: theme.colors.textTertiary, opacity: 0.3 }} />
              <div style={{ height: '6px', borderRadius: theme.shape?.radiusSm || '2px', background: theme.colors.textTertiary, opacity: 0.3 }} />
            </div>

            {/* Main content */}
            <div style={{ flex: 1, padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {/* Title */}
              <div style={{
                height: '8px',
                width: '50%',
                background: theme.colors.textPrimary,
                opacity: 0.8,
                borderRadius: '2px'
              }} />

              {/* Card */}
              <div
                style={{
                  flex: 1,
                  background: theme.colors.bgCard,
                  borderRadius: theme.shape?.radiusCard || '4px',
                  boxShadow: theme.shadows?.card || '0 1px 3px rgba(0,0,0,0.1)',
                  border: theme.id === 'fusion' ? 'none' : `1px solid ${theme.colors.borderPrimary}`,
                  borderBottom: theme.id === 'fusion' ? `1px solid ${theme.colors.borderPrimary}` : undefined,
                  padding: '6px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                {/* Card content */}
                <div style={{ height: '5px', width: '60%', background: theme.colors.textPrimary, opacity: 0.6, borderRadius: '2px' }} />
                <div style={{ height: '4px', width: '80%', background: theme.colors.textTertiary, opacity: 0.4, borderRadius: '1px' }} />
                <div style={{ height: '4px', width: '70%', background: theme.colors.textTertiary, opacity: 0.3, borderRadius: '1px' }} />

                <div style={{ flex: 1 }} />

                {/* Button */}
                <div style={{
                  height: '10px',
                  width: '35%',
                  background: theme.colors.primary,
                  borderRadius: theme.shape?.radiusButton || '2px'
                }} />
              </div>

              {/* Bottom stats */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {['success', 'warning', 'info'].map((status, i) => (
                  <div key={i} style={{
                    height: '16px',
                    flex: 1,
                    background: theme.colors[`${status}Light`] || theme.colors.bgTertiary,
                    borderRadius: theme.shape?.radiusSm || '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '60%',
                      height: '4px',
                      background: theme.colors[status] || theme.colors.primary,
                      borderRadius: '1px',
                      opacity: 0.8
                    }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Theme info */}
      <div className="theme-card-info">
        <div className="theme-card-header">
          <span className="theme-icon">{theme.icon}</span>
          <div className="theme-names">
            <h3 className="theme-name">{theme.nameHe}</h3>
            <span className="theme-name-en">{theme.name}</span>
          </div>
          {isCurrent && (
            <span className="current-badge">נוכחי</span>
          )}
        </div>
        <p className="theme-description">{theme.descriptionHe}</p>

        {/* Style highlights */}
        <div className="theme-highlights">
          <span className="highlight-item" style={{ background: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
            {theme.isDark ? '🌙 כהה' : '☀️ בהיר'}
          </span>
          <span className="highlight-item" style={{ background: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
            {theme.shape?.radiusCard === '0px' ? '▢ חד' : theme.shape?.radiusCard === '16px' ? '◯ מעוגל' : '⬡ רגיל'}
          </span>
          <span className="highlight-item" style={{ background: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
            {theme.components?.cardPadding === '12px' ? '📊 צפוף' : theme.components?.cardPadding === '24px' ? '🌿 מרווח' : '📋 סטנדרט'}
          </span>
        </div>

        {/* Color palette */}
        <div className="theme-colors">
          <span
            className="color-swatch large"
            style={{ background: theme.colors.primary }}
            title="צבע ראשי"
          />
          <span
            className="color-swatch"
            style={{ background: theme.colors.bgHeader }}
            title="רקע כותרת"
          />
          <span
            className="color-swatch"
            style={{ background: theme.colors.bgCard }}
            title="רקע כרטיס"
          />
          <span
            className="color-swatch"
            style={{ background: theme.colors.success }}
            title="הצלחה"
          />
          <span
            className="color-swatch"
            style={{ background: theme.colors.textPrimary }}
            title="טקסט"
          />
        </div>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="selection-indicator">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M15 5L7 13L3 9"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <style jsx>{`
        .theme-card {
          position: relative;
          display: flex;
          flex-direction: column;
          background: var(--color-bg-secondary, #fff);
          border: 2px solid var(--color-border-primary, #e5e7eb);
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.25s ease;
          text-align: right;
        }

        .theme-card:hover {
          border-color: var(--color-primary, #3b82f6);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          transform: translateY(-3px);
        }

        .theme-card.selected {
          border-color: var(--color-primary, #3b82f6);
          box-shadow: 0 0 0 4px var(--color-primary-light, rgba(59, 130, 246, 0.15));
        }

        .theme-card.current {
          border-color: var(--color-success, #10b981);
        }

        /* Preview area */
        .theme-card-preview {
          height: 130px;
          padding: 8px;
          overflow: hidden;
        }

        .preview-page {
          width: 100%;
          height: 100%;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        /* Info section */
        .theme-card-info {
          padding: 16px;
          border-top: 1px solid var(--color-border-primary, #e5e7eb);
        }

        .theme-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }

        .theme-icon {
          font-size: 24px;
        }

        .theme-names {
          flex: 1;
        }

        .theme-name {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: var(--color-text-primary, #111);
        }

        .theme-name-en {
          font-size: 12px;
          color: var(--color-text-tertiary, #6b7280);
        }

        .current-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 4px 10px;
          background: var(--color-success-light, #d1fae5);
          color: var(--color-success, #10b981);
          border-radius: 6px;
        }

        .theme-description {
          margin: 0 0 12px;
          font-size: 13px;
          color: var(--color-text-secondary, #4b5563);
          line-height: 1.5;
        }

        /* Style highlights */
        .theme-highlights {
          display: flex;
          gap: 6px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .highlight-item {
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 6px;
          color: var(--color-text-secondary, #4b5563);
        }

        /* Color swatches */
        .theme-colors {
          display: flex;
          gap: 6px;
          align-items: center;
        }

        .color-swatch {
          width: 20px;
          height: 20px;
          border-radius: 6px;
          border: 2px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          transition: transform 0.15s;
        }

        .color-swatch.large {
          width: 26px;
          height: 26px;
        }

        .color-swatch:hover {
          transform: scale(1.15);
        }

        /* Selection indicator */
        .selection-indicator {
          position: absolute;
          top: 12px;
          left: 12px;
          width: 28px;
          height: 28px;
          background: var(--color-primary, #3b82f6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: scaleIn 0.2s ease;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        }

        @keyframes scaleIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
      `}</style>
    </button>
  );
};

export default ThemeSelectorPanel;
